import { ExpensePaymentFlowInformationEnum } from "@finway-group/shared/lib/models/expense/expensePaymentFlowInformation.enum"
import { VendorRule } from "@finway-group/shared/lib/models/vendor/vendorRule.model"
import { Button, Col, Form, Input, Modal, Radio, Row } from "antd"
import { FormSelectOption } from "Components/form/form.types"
import FormSelect from "Components/form/formSelect"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useCompany } from "Shared/hooks/company.hooks"

import { useCostCenterById, useSubCostCenters } from "Shared/hooks/costCenter.hooks"
// hooks import
import { useCostCenter2ById, useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useExpenseAccountById, useExpenseAccounts } from "Shared/hooks/expenseAccount.hooks"
import { useVendor } from "Shared/hooks/vendor.hooks"
// service import
import { NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { updateVendorRule } from "Shared/store/actions/vendor/vendorActions"
import { createDynamicList, getDynamicCostCenter2List, getDynamicCostCenterList, getDynamicExpenseAccountList } from "Shared/utils/filter.utils"
// utils import
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

interface VendorRuleModalInterface {
    isVisible: boolean
    rule?: VendorRule
    onCancel: (state: boolean) => void
}

enum VendorRulePaymentTermEnum {
    SEVEN_DAYS = 7,
    FOURTEEN_DAYS = 14,
    TWENTY_EIGHT_DAYS = 28,
}

const VendorRuleModal: React.FC<VendorRuleModalInterface> = ({ isVisible, onCancel, rule }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const initialRule: Pick<VendorRule, "costCenter" | "costCenter2" | "expenseAccount" | "expensePaymentFlowInformation" | "paymentTerm"> = {
        // Empty strings for fallback value in the forms
        costCenter: useCostCenterById(rule?.costCenter, true)?._id ?? "",
        costCenter2: useCostCenter2ById(rule?.costCenter2)?._id ?? "",
        expenseAccount: useExpenseAccountById(rule?.expenseAccount)?._id ?? "",
        expensePaymentFlowInformation: rule?.expensePaymentFlowInformation,
        paymentTerm: rule?.paymentTerm,
    }

    const [ruleToUpdate, setRuleToUpdate] = useState(initialRule)

    // predefined options for payment terms
    const paymentTermOptions = [VendorRulePaymentTermEnum.SEVEN_DAYS, VendorRulePaymentTermEnum.FOURTEEN_DAYS, VendorRulePaymentTermEnum.TWENTY_EIGHT_DAYS]

    const [formInstance] = Form.useForm()
    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)

    // states relevant for the payment term handling
    const [paymentTermRadio, setPaymentTermRadio] = useState<number | undefined>(undefined)
    const [paymentTermInput, setPaymentTermInput] = useState<number | undefined>(undefined)

    const costCenters = useSubCostCenters(true)
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()
    const vendor = useVendor()
    const { showCostCentersIds } = useCompany()

    useEffect(() => {
        if (isVisible && ruleToUpdate) {
            formInstance.setFieldsValue(ruleToUpdate)
            paymentTermIsInOptions ? onPaymentTermRadioChange(ruleToUpdate.paymentTerm) : onPaymentTermInputChange(ruleToUpdate.paymentTerm)
        }

        return () => {
            if (isVisible) formInstance.resetFields()
        }
    }, [isVisible])

    const handleHide = () => {
        setIsLoading(false)
        formInstance.resetFields()
        onCancel(false)
    }

    const onSubmit = async () => {
        setIsLoading(true)
        try {
            const vendorId = vendor && vendor.id

            // Clean up empty strings in the vendor rules before performing update
            const rule: Partial<VendorRule> = {
                ...ruleToUpdate,
                costCenter: handleEmpty(ruleToUpdate.costCenter),
                costCenter2: handleEmpty(ruleToUpdate.costCenter2),
                expenseAccount: handleEmpty(ruleToUpdate.expenseAccount),
            }

            await updateVendorRule(vendorId, { rule })(dispatch)

            handleHide()
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.updated.title"), t("notification:vendor.updated.message"))
        } catch (err) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:vendor.edit.title"))
        }
    }

    // update rule attributes
    const handleEmpty = (value?: string) => (value && value !== "" ? value : undefined)
    const onCostCenterChange = (costCenterParam?: string) => setRuleToUpdate({ ...ruleToUpdate, costCenter: costCenterParam })
    const onCostCenter2Change = (costCenter2Param?: string) => setRuleToUpdate({ ...ruleToUpdate, costCenter2: costCenter2Param })
    const onExpenseAccountChange = (expenseAccountParam?: string) => setRuleToUpdate({ ...ruleToUpdate, expenseAccount: expenseAccountParam })
    const onExpensePaymentFlowInformationChange = (expensePaymentFlowInformationParam?: ExpensePaymentFlowInformationEnum) =>
        setRuleToUpdate({ ...ruleToUpdate, expensePaymentFlowInformation: expensePaymentFlowInformationParam })
    const onPaymentTermChange = (paymentTerm?: number) => setRuleToUpdate({ ...ruleToUpdate, paymentTerm })

    // handle values to show payment term on radio button or input
    const onPaymentTermRadioChange = (paymentTerm?: number) => {
        setPaymentTermInput(undefined)
        setPaymentTermRadio(paymentTerm)
    }

    const onPaymentTermInputChange = (paymentTerm?: number) => {
        setPaymentTermRadio(undefined)
        setPaymentTermInput(paymentTerm)
    }

    const onDeselectRadio = (e: any) => {
        if (e.target?.value == ruleToUpdate.paymentTerm) {
            setPaymentTermRadio(undefined)
            onPaymentTermChange(undefined)
        }
    }

    const paymentStatusList: FormSelectOption[] = createDynamicList("input:request.payment_flow_information", [
        ExpensePaymentFlowInformationEnum.ALREADY_PAID,
        ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID,
        ExpensePaymentFlowInformationEnum.REPORTING_ONLY,
    ])

    // check if payment term is one of the options
    const paymentTermIsInOptions = paymentTermOptions.filter((paymentTermParam: number) => paymentTermParam === ruleToUpdate.paymentTerm).length > 0

    const resetFields = () => setRuleToUpdate(initialRule)

    return (
        <Modal
            destroyOnClose={true}
            afterClose={() => formInstance.resetFields()}
            visible={isVisible}
            maskClosable={false}
            title={t("label:set_vendor_rule")}
            onCancel={() => {
                resetFields()
                onCancel(false)
            }}
            closable={true}
            keyboard={true}
            className="ant-modal--small"
            footer={[
                <Button
                    key="back"
                    onClick={() => {
                        resetFields()
                        onCancel(false)
                    }}
                >
                    {t("action:cancel")}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={isLoading}
                    onClick={() => {
                        formInstance.submit()
                    }}
                >
                    {t("action:vendor.set_rule")}
                </Button>,
            ]}
        >
            <div>
                <Form layout="vertical" form={formInstance} onFinish={onSubmit} scrollToFirstError={true}>
                    <FormSelect
                        name="costCenter"
                        header={t("input:request.cost_center")}
                        initialValue={ruleToUpdate?.costCenter}
                        options={getDynamicCostCenterList(costCenters, showCostCentersIds)}
                        fallback={String(t("missing:request.cost_center_1"))}
                        onChange={onCostCenterChange}
                        showSearch
                        isForm
                    />

                    {costCenters2.length > 0 && (
                        <FormSelect
                            name="costCenter2"
                            header={t("label:cost_center_2")}
                            initialValue={ruleToUpdate?.costCenter2}
                            options={getDynamicCostCenter2List(costCenters2)}
                            fallback={String(t("missing:request.cost_center_2"))}
                            onChange={onCostCenter2Change}
                            showSearch
                            isForm
                        />
                    )}
                    {expenseAccounts.length > 0 && (
                        <FormSelect
                            name="expenseAccount"
                            header={t("label:expense_account")}
                            initialValue={ruleToUpdate?.expenseAccount}
                            options={getDynamicExpenseAccountList(expenseAccounts)}
                            fallback={String(t("missing:request.expense_account"))}
                            onChange={onExpenseAccountChange}
                            showSearch
                            isForm
                        />
                    )}

                    <FormSelect
                        name="paymentFlowInformation"
                        header={t("label:payment_flow_information.title")}
                        initialValue={ruleToUpdate?.expensePaymentFlowInformation}
                        options={paymentStatusList}
                        fallback={String(t("input:request.payment_flow_information.not_set"))}
                        onChange={onExpensePaymentFlowInformationChange}
                        showSearch
                        isForm
                    />

                    <Row gutter={[16, 16]}>
                        <Col span={24} sm="24">
                            <Form.Item label={t("label:payment_term_desc")} key="paymentTerm">
                                <div className="custom-payment-term-radio flex flex-row">
                                    <Radio.Group
                                        className="flex-col min-w-300"
                                        onChange={(e) => {
                                            onPaymentTermRadioChange(e.target.value)
                                            onPaymentTermChange(e.target.value)
                                        }}
                                        value={paymentTermRadio}
                                        optionType="button"
                                    >
                                        {paymentTermOptions.map((value, index) => {
                                            return (
                                                <Radio.Button key={index} onClick={onDeselectRadio} value={value}>
                                                    {value}
                                                </Radio.Button>
                                            )
                                        })}
                                    </Radio.Group>
                                    <Input
                                        className="ml-20 text-center"
                                        type="number"
                                        min={0}
                                        value={paymentTermInput}
                                        onChange={(e) => {
                                            onPaymentTermInputChange(Number(e.target.value))
                                            onPaymentTermChange(Number(e.target.value))
                                        }}
                                        bordered={false}
                                    />
                                </div>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>
        </Modal>
    )
}

export default VendorRuleModal
